import { useEffect, useState } from "react";
import { Calendar, Clock } from "@phosphor-icons/react";
import { useT } from "../../contexts/LanguageContext";

const EXAM_DATE_KEY = "pmpintel_exam_date_v1";

function daysBetween(target) {
  const now = new Date();
  const t = new Date(target);
  const diff = Math.ceil((t.setHours(0, 0, 0, 0) - now.setHours(0, 0, 0, 0)) / 86400000);
  return diff;
}

export default function ExamCountdown() {
  const { t, lang } = useT();
  const [examDate, setExamDate] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  useEffect(() => {
    try {
      const stored = localStorage.getItem(EXAM_DATE_KEY);
      if (stored) {
        setExamDate(stored);
        setDraft(stored);
      } else {
        setEditing(true);
      }
    } catch { /* noop */ }
  }, []);

  const save = () => {
    if (!draft) return;
    try { localStorage.setItem(EXAM_DATE_KEY, draft); } catch { /* noop */ }
    setExamDate(draft);
    setEditing(false);
  };

  const clear = () => {
    try { localStorage.removeItem(EXAM_DATE_KEY); } catch { /* noop */ }
    setExamDate(null);
    setDraft("");
    setEditing(true);
  };

  const days = examDate ? daysBetween(examDate) : null;

  // Visual urgency colour ramp
  const urgency = days == null ? "default" : days < 0 ? "past" : days <= 7 ? "high" : days <= 30 ? "mid" : "low";
  const palette = {
    default: { bg: "bg-slate-50", border: "border-slate-200", accent: "text-slate-700" },
    past: { bg: "bg-slate-100", border: "border-slate-300", accent: "text-slate-500" },
    high: { bg: "bg-red-50", border: "border-red-200", accent: "text-red-700" },
    mid: { bg: "bg-amber-50", border: "border-amber-200", accent: "text-amber-700" },
    low: { bg: "bg-emerald-50", border: "border-emerald-200", accent: "text-emerald-700" },
  }[urgency];

  const formatted = examDate
    ? new Date(examDate).toLocaleDateString(lang === "tr" ? "tr-TR" : "en-US", { year: "numeric", month: "long", day: "numeric" })
    : null;

  return (
    <div className={`card !p-4 ${palette.bg} ${palette.border}`} data-testid="exam-countdown">
      <div className="flex items-start justify-between gap-3">
        <div className="overline flex items-center gap-1.5"><Calendar size={12} weight="fill" /> {t("countdown.title")}</div>
        {examDate && !editing && (
          <button onClick={() => setEditing(true)} className="text-[10px] font-mono uppercase tracking-wider text-slate-500 hover:text-[#002FA7]" data-testid="countdown-edit">
            {t("countdown.edit")}
          </button>
        )}
      </div>

      {editing ? (
        <div className="mt-3 space-y-2">
          <input
            type="date"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            min={new Date().toISOString().split("T")[0]}
            className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:border-[#002FA7]"
            data-testid="countdown-date-input"
          />
          <div className="flex gap-2">
            <button onClick={save} disabled={!draft} className="btn-primary !py-1.5 text-xs flex-1 disabled:opacity-50" data-testid="countdown-save">
              {t("countdown.save")}
            </button>
            {examDate && (
              <button onClick={() => setEditing(false)} className="btn-secondary !py-1.5 text-xs" data-testid="countdown-cancel">
                {t("common.cancel")}
              </button>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="mt-2 flex items-baseline gap-2">
            <span className={`font-display text-4xl font-bold ${palette.accent}`} data-testid="countdown-days">
              {days < 0 ? "—" : days}
            </span>
            <span className="text-sm text-slate-600">
              {days < 0 ? t("countdown.past_exam") : days === 0 ? t("countdown.today") : t("countdown.days_left")}
            </span>
          </div>
          <div className="text-xs text-slate-500 mt-2 flex items-center gap-1">
            <Clock size={11} />
            <span>{formatted}</span>
            <button onClick={clear} className="ml-auto text-slate-400 hover:text-red-500" data-testid="countdown-clear">
              {t("countdown.clear")}
            </button>
          </div>
          {urgency === "high" && (
            <div className="mt-3 text-xs text-red-700 font-medium" data-testid="countdown-urgency">
              {t("countdown.urgent_message")}
            </div>
          )}
        </>
      )}
    </div>
  );
}
